/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CallerIdLookupSourcesFormView.tsx
 🗓️ Created: 2025-01-28
 🧩 Description:
    Form view for creating and editing Caller ID Lookup Sources.
    Manages lookup source configuration with General and Advanced Settings sections.

 🔧 Notes:
    - Domain-scoped data
    - Supports JSONB settings for advanced configuration
    - Conditional fields based on source type

 🔁 Last Updated: 2025-01-28 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { Plus, Search, Copy } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { FormBox } from '../../components/ui/FormBox';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { navigateToCallerIdLookupSourceDuplicate } from './callerIdLookupSourcesActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { fetchLookupSource, createLookupSource, updateLookupSource, fetchLookupSources, deleteLookupSource } from './api';
import { CallerIdLookupSource, LookupSourceFormState } from './types';
import { CallerIdLookupGeneralSection } from './components/CallerIdLookupGeneralSection';

export default function CallerIdLookupSourcesFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError, showInfo } = useNotifications();

  // Refs to prevent infinite loops
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const currentIdRef = useRef<string | undefined>(undefined);
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Update refs when values change
  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  const [isLoading, setIsLoading] = useState(!!duplicateId);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Quick Search state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; name: string; description?: string | null }>>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Form data
  const [formData, setFormData] = useState<LookupSourceFormState>({
    name: '',
    sourceType: 'internal',
    url: '',
    httpMethod: 'GET',
    authUsername: '',
    authPassword: '',
    token: '',
    timeout: 5,
    cacheTtl: 300,
    responseMapping: {},
    settings: {},
    enabled: true
  });

  // Track original data
  const [originalData, setOriginalData] = useState<LookupSourceFormState>({
    name: '',
    sourceType: 'internal',
    url: '',
    httpMethod: 'GET',
    authUsername: '',
    authPassword: '',
    token: '',
    timeout: 5,
    cacheTtl: 300,
    responseMapping: {},
    settings: {},
    enabled: true
  });

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData({
        name: '',
        sourceType: 'internal',
        url: '',
        httpMethod: 'GET',
        authUsername: '',
        authPassword: '',
        token: '',
        timeout: 5,
        cacheTtl: 300,
        responseMapping: {},
        settings: {},
        enabled: true
      });
      setOriginalData({
        name: '',
        sourceType: 'internal',
        url: '',
        httpMethod: 'GET',
        authUsername: '',
        authPassword: '',
        token: '',
        timeout: 5,
        cacheTtl: 300,
        responseMapping: {},
        settings: {},
        enabled: true
      });
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, resetParam, searchParams]);

  // Load lookup source if editing or duplicating
  const loadSource = useCallback(async (duplicate: boolean = false) => {
    const loadId = duplicate ? duplicateId : id;
    if (!loadId || loadId === 'new') {
      setIsLoading(false);
      isLoadingRef.current = false;
      return;
    }

    // Prevent loading the same ID multiple times
    if (lastLoadedIdRef.current === loadId && !duplicate) {
      setIsLoading(false);
      isLoadingRef.current = false;
      return;
    }

    if (isLoadingRef.current) {
      return;
    }

    setIsLoading(true);
    isLoadingRef.current = true;
    lastLoadedIdRef.current = loadId;

    try {
      const source = await fetchLookupSource(loadId);

      if (source) {

        const loadedData = {
          name: duplicate ? `${source.name || ''} (Copy)` : (source.name || ''),
          sourceType: source.sourceType || 'internal',
          url: source.url || '',
          httpMethod: source.httpMethod || 'GET',
          authUsername: source.authUsername || '',
          authPassword: source.authPassword || '',
          token: source.token || '',
          timeout: source.timeout ?? 5,
          cacheTtl: source.cacheTtl ?? 300,
          responseMapping: source.responseMapping || {},
          settings: source.settings || {},
          enabled: source.enabled ?? true
        };

        // Use setTimeout to batch state updates
        setTimeout(() => {
          setFormData(loadedData);
          // If duplicating, reset originalData to treat as new entry
          if (duplicate) {
            setOriginalData({
              name: '',
              sourceType: 'internal',
              url: '',
              httpMethod: 'GET',
              authUsername: '',
              authPassword: '',
              token: '',
              timeout: 5,
              cacheTtl: 300,
              responseMapping: {},
              settings: {},
              enabled: true
            });
          } else {
            setOriginalData(loadedData);
          }
        }, 0);
      }
    } catch (error) {
      lastLoadedIdRef.current = null;
      showErrorRef.current(tRef.current('callerIdLookupSources.messages.loadError'));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        isLoadingRef.current = false;
      }, 0);
    }
  }, [id, duplicateId]);

  // Reset load guard on unmount — ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  // Main effect to load data
  useEffect(() => {
    const idChanged = currentIdRef.current !== id;
    currentIdRef.current = id;

    if (idChanged || !hasInitializedRef.current) {
      if (isEditMode) {
        hasInitializedRef.current = true;
        loadSource(false);
      } else if (duplicateId) {
        hasInitializedRef.current = true;
        loadSource(true);
      } else {
        hasInitializedRef.current = true;
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    }
  }, [id, isEditMode, duplicateId, loadSource]);

  // Handle field changes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = t('callerIdLookupSources.validation.nameRequired');
    }

    if (!formData.sourceType) {
      newErrors.sourceType = t('callerIdLookupSources.validation.sourceTypeRequired');
    }

    // Validate URL for types that require it
    if (['http', 'ldap', 'database', 'opencnam'].includes(formData.sourceType)) {
      if (!formData.url || formData.url.trim().length === 0) {
        newErrors.url = t('callerIdLookupSources.validation.urlRequired');
      }
    }

    if (formData.timeout < 1 || formData.timeout > 30) {
      newErrors.timeout = t('callerIdLookupSources.validation.timeoutInvalid');
    }

    if (formData.cacheTtl < 0 || formData.cacheTtl > 3600) {
      newErrors.cacheTtl = t('callerIdLookupSources.validation.cacheTtlInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  // Handle form submission
  const handleSubmit = useCallback(async (e?: React.FormEvent): Promise<CallerIdLookupSource | undefined> => {
    e?.preventDefault();

    if (!validateForm()) {
      showErrorRef.current(tRef.current('common.validationError'));
      return undefined;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        sourceType: formData.sourceType,
        url: formData.url?.trim() || null,
        httpMethod: formData.httpMethod,
        authUsername: formData.authUsername?.trim() || null,
        authPassword: formData.authPassword?.trim() || null,
        token: formData.token?.trim() || null,
        timeout: formData.timeout,
        cacheTtl: formData.cacheTtl,
        responseMapping: formData.responseMapping,
        settings: formData.settings,
        enabled: formData.enabled
      };

      const result = isEditMode
        ? await updateLookupSource(id!, payload)
        : await createLookupSource(payload);

      setTimeout(() => {
        setOriginalData({ ...formData });
      }, 0);

      showSuccessRef.current(
        isEditMode
          ? tRef.current('callerIdLookupSources.messages.updateSuccess')
          : tRef.current('callerIdLookupSources.messages.createSuccess')
      );

      // Return saved data for useSaveWithAction
      return result;
    } catch (error) {
      showErrorRef.current(error.message || tRef.current('callerIdLookupSources.messages.createError'));
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [validateForm, formData, originalData, isEditMode, id]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as CallerIdLookupSource;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/callerid-lookup-sources',
    newPath: '/pbx/incoming-tools/callerid-lookup-sources/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // CallerIdLookupSources use ID for navigation
      return `/pbx/incoming-tools/callerid-lookup-sources/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'callerIdLookupSources:saveAndNewData'
  });

  // Handle cancel
  const handleCancel = useCallback(() => {
    navigateRef.current('/pbx/incoming-tools/callerid-lookup-sources');
  }, []);

  // Handle delete
  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  // Confirm delete
  const confirmDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteLookupSource(id);
      showSuccessRef.current(tRef.current('callerIdLookupSources.messages.deleteSuccess'));
      setShowDeleteModal(false);
      navigateRef.current('/pbx/incoming-tools/callerid-lookup-sources');
    } catch (error) {
      showErrorRef.current(error.message || tRef.current('callerIdLookupSources.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [id]);

  // Handle duplicate from form
  const handleDuplicateFromForm = useCallback(() => {
    if (id && id !== 'new') {
      navigateToCallerIdLookupSourceDuplicate(navigateRef.current, id);
    }
  }, [id]);

  // Load quick list
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const result = await fetchLookupSources({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      const items = Array.isArray(result?.data)
        ? (result.data as Array<{ id: number; name: string; description?: string | null }>)
        : [];
      setQuickListItems(items);
    } catch {
      showErrorRef.current(tRef.current('callerIdLookupSources.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  // Filter quick list
  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.description ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }



  return (
    <div className="p-6"><ModuleFormHeader
      icon={<Search className="h-4 w-4 text-primary" />}
      title={isEditMode
        ? `${t('callerIdLookupSources.editEntry')} - ${formData.name || ''}`
        : t('callerIdLookupSources.createEntry')
      }
      backButtonText="List"
      onBack={handleCancel}
      actions={[
        {
          icon: <Search className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('common.search', 'Search'),
          onClick: () => {
            setShowQuickList(true);
            void loadQuickList();
          },
        },
        ...(isEditMode && !duplicateId
          ? [{
            icon: <Copy className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('common.duplicate'),
            onClick: handleDuplicateFromForm,
          }]
          : []),
        {
          icon: <Plus className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('callerIdLookupSources.actions.add'),
          onClick: () => navigate('/pbx/tools/caller-id-lookup-sources/new'),
        },
      ]}
    />

      <div className="mt-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6 pb-20">
            {/* General Section */}
            <CallerIdLookupGeneralSection
              formData={formData}
              errors={errors}
              onFieldChange={handleChange}
              t={t}
            />
          </div>

          <FixedActionBar
            onSaveWithAction={handleSaveWithAction}
            onCancel={handleCancel}
            onDelete={isEditMode ? handleDelete : undefined}
            isEditMode={isEditMode}
            isSaving={isSaving}
            isLoading={isLoading}
            saveText={t('common.save')}
            cancelText={t('common.cancel')}
            deleteText={t('common.delete')}
            useSaveDropdown={true}
            saveDropdownStorageKey="callerIdLookupSources:saveActionPreference"
          />
        </form>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('callerIdLookupSources.messages.deleteTitle')}
        message={t('callerIdLookupSources.messages.confirmDelete', { name: formData.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('callerIdLookupSources.quickList.title', 'Caller ID Lookup Sources')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigateRef.current(`/pbx/incoming-tools/callerid-lookup-sources/${item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigateRef.current(`/pbx/incoming-tools/callerid-lookup-sources/${item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('callerIdLookupSources.quickList.searchPlaceholder', 'Search by name or description')}
        emptyMessage={t('callerIdLookupSources.quickList.empty', 'No Caller ID Lookup Sources found')}
        headerIcon={<Search className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

